/**
 * `<caos-message-bubble>` — one message in a conversation: who said it, when, and what they said. (Catalog: Conversation › MessageBubble — one message, with its author, its time and its body.)
 *
 * The unit the whole conversation set is built out of. It draws one message and nothing around it:
 * the stream above owns the order, the day dividers and the unread line.
 *
 * ── GROUPED, which is most of what this component is for ─────────────────────────────────────
 * `grouped` says this message continues the one above it — same person, a moment later. A grouped
 * message draws its body alone, with no avatar, name or time, because the message above already
 * said all three. A conversation without this is a column of identical headers with a sentence
 * under each, and it is the single largest difference between a message list that reads like a
 * conversation and one that reads like a log file.
 *
 * The decision is NOT made here. `continuesPrevious()` in `message-format.ts` is the rule, and the
 * STREAM applies it — because the answer depends on the message above, which this element cannot
 * see. A component that guessed from its own DOM position would be wrong the first time a divider
 * was drawn between two messages from one author.
 *
 * ── The body is markdown, and it is drawn as nodes ───────────────────────────────────────────
 * Through `renderMessageBody()`, which never builds an HTML string — see that module for why, and
 * for which addresses a link may point at. `body` is a PROPERTY as well as an attribute: a message
 * may be longer than an attribute should carry, and it may contain newlines, which an attribute
 * cannot carry at all without being re-parsed.
 *
 * ── What it deliberately does not have ───────────────────────────────────────────────────────
 * No action buttons, no reaction picker, no menu. A message that can execute something is an
 * app-specific extension of a message — the schema of the action belongs to whatever emitted it —
 * and it goes in the `footer` slot rather than in the library. That is the same boundary the
 * discovery of the reference surface drew, and the reason this component has a slot there at all.
 *
 * Styling seams: `::part(message)`, `::part(avatar)`, `::part(header)`, `::part(author)`,
 * `::part(time)`, `::part(body)`, `::part(footer)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { MESSAGE_BODY_CSS, renderMessageBody, type MessageBodyOptions } from '../message-body.js';
import { timeLabel } from '../message-format.js';
// One answer in this library to "what letters stand for this name" — see `<caos-avatar>`.
import { initialsOf } from './avatar.js';

const sheet = makeSheet(`
  :host { display: block; }

  .message {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--caos-space-2);
    padding: var(--caos-space-1) var(--caos-space-3);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    color: var(--caos-color-text);
  }
  :host([grouped]) .message { padding-top: 0; }

  /* The avatar column keeps its width when grouped, so bodies stay in one line down the page. */
  .avatar {
    width: 28px;
    height: 28px;
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-surface-2);
    border: 1px solid var(--caos-color-border);
    color: var(--caos-color-text-muted);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: var(--caos-text-label);
    font-weight: var(--caos-font-weight-label);
    text-transform: uppercase;
    user-select: none;
  }
  .avatar[data-empty="true"] { background: transparent; border-color: transparent; }

  .header {
    display: flex;
    align-items: baseline;
    gap: var(--caos-space-2);
    margin-bottom: 2px;
  }
  .author { font-weight: var(--caos-font-weight-label); }
  .time { color: var(--caos-color-text-subtle); font-size: var(--caos-text-label); }

  .body { min-width: 0; overflow-wrap: anywhere; }

  .more {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    margin-top: var(--caos-space-1);
    color: var(--caos-color-accent);
    font: inherit;
    font-size: var(--caos-text-label);
    cursor: pointer;
  }
  .more:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* A message arrived at from a search or a link says so, briefly. Held by an attribute rather
     than by an animation that has finished, so a reader who was away still sees which one it was. */
  :host([highlighted]) .message { background: var(--caos-color-surface-2); }

  ::slotted([slot="footer"]) { display: block; margin-top: var(--caos-space-1); }

${MESSAGE_BODY_CSS}
`);

/** How much of a long body is shown before it is folded. */
export const MESSAGE_FOLD_CHARS = 900;

export class CaosMessageBubble extends CaosElement {
  static get observedAttributes(): string[] {
    return ['author', 'at', 'body', 'grouped', 'avatar', 'highlighted'];
  }

  private readonly message: HTMLDivElement;
  private readonly avatar: HTMLSpanElement;
  private readonly header: HTMLDivElement;
  private readonly authorEl: HTMLSpanElement;
  private readonly timeEl: HTMLTimeElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly more: HTMLButtonElement;
  private _body: string | null = null;
  private _absentPeople: string[] = [];
  private expanded = false;

  constructor() {
    super(sheet);
    this.message = document.createElement('div');
    this.message.className = 'message';
    this.message.setAttribute('part', 'message');

    this.avatar = document.createElement('span');
    this.avatar.className = 'avatar';
    this.avatar.setAttribute('part', 'avatar');
    // The initials repeat the author's name, which the header already carries; a screen reader
    // reading both says the same person twice before the message.
    this.avatar.setAttribute('aria-hidden', 'true');

    const column = document.createElement('div');
    column.style.minWidth = '0';

    this.header = document.createElement('div');
    this.header.className = 'header';
    this.header.setAttribute('part', 'header');
    this.authorEl = document.createElement('span');
    this.authorEl.className = 'author';
    this.authorEl.setAttribute('part', 'author');
    this.timeEl = document.createElement('time');
    this.timeEl.className = 'time';
    this.timeEl.setAttribute('part', 'time');
    this.header.append(this.authorEl, this.timeEl);

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'body';
    this.bodyEl.setAttribute('part', 'body');

    this.more = document.createElement('button');
    this.more.type = 'button';
    this.more.className = 'more';
    this.more.hidden = true;
    this.more.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.renderBody();
    });

    const footer = document.createElement('slot');
    footer.name = 'footer';
    footer.setAttribute('part', 'footer');

    column.append(this.header, this.bodyEl, this.more, footer);
    this.message.append(this.avatar, column);
    this.root.append(this.message);
  }

  connectedCallback(): void {
    // An article, so a screen reader can move message by message rather than by paragraph.
    if (!this.hasAttribute('role')) this.setAttribute('role', 'article');
    this.render();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'body') this._body = null;
    this.render();
  }

  /** Who said it. */
  get author(): string {
    return this.getAttribute('author') ?? '';
  }
  set author(value: string) {
    this.setAttribute('author', value);
  }

  /** When, as an ISO-8601 instant. Anything the runtime cannot read as a date draws no time. */
  get at(): string {
    return this.getAttribute('at') ?? '';
  }
  set at(value: string) {
    this.setAttribute('at', value);
  }

  /**
   * What they said, as markdown.
   *
   * A property as well as an attribute, and the property WINS. A message carries newlines and can
   * run long; an attribute holding one is re-parsed by the browser's own attribute rules before
   * anything here sees it.
   */
  get body(): string {
    return this._body ?? this.getAttribute('body') ?? '';
  }
  set body(value: string) {
    this._body = value;
    this.render();
  }

  /**
   * Principal ids of mentioned people who are no longer in this workspace (CAOS-455).
   *
   * A property only, never an attribute: a list of ids is not a thing to spell into markup, and
   * the surface that knows the answer got it from `mentions resolve` in code rather than from a
   * template. Empty — the default — means nobody has been reported absent, which is why the
   * polarity is absence and not presence: see {@link MessageBodyOptions}.
   */
  get absentPeople(): readonly string[] {
    return this._absentPeople;
  }
  set absentPeople(value: Iterable<string>) {
    this._absentPeople = [...(value ?? [])];
    this.renderBody();
  }

  /** This message continues the one above it: no avatar, no name, no time. */
  get grouped(): boolean {
    return this.hasAttribute('grouped');
  }
  set grouped(value: boolean) {
    this.toggleAttribute('grouped', value);
  }

  private render(): void {
    const grouped = this.grouped;
    this.header.hidden = grouped;

    const author = this.author.trim();
    this.authorEl.textContent = author;
    // The initials, when nothing better was given — the same letters `<caos-avatar>` would draw for
    // this person, so the two never disagree about somebody on one screen.
    const avatar = this.getAttribute('avatar');
    const initials = initialsOf(author);
    this.avatar.textContent = grouped ? '' : (avatar ?? initials);
    this.avatar.dataset['empty'] = String(grouped || (avatar ?? initials) === '');

    const at = this.at;
    const parsed = at ? new Date(at) : null;
    const valid = parsed !== null && !Number.isNaN(parsed.getTime());
    this.timeEl.hidden = !valid;
    if (valid && parsed) {
      this.timeEl.dateTime = parsed.toISOString();
      this.timeEl.textContent = timeLabel(parsed);
    }

    this.renderBody();
  }

  private renderBody(): void {
    const body = this.body;
    const long = body.length > MESSAGE_FOLD_CHARS;
    // Folded at a paragraph break where there is one near the limit, so the fold does not cut a
    // sentence in half. A message folded mid-word reads as a rendering failure rather than a fold.
    const shown = !long || this.expanded ? body : foldAt(body, MESSAGE_FOLD_CHARS);
    const opts: MessageBodyOptions = { absentPeople: this._absentPeople };
    this.bodyEl.replaceChildren(renderMessageBody(shown, this.ownerDocument, opts));
    this.more.hidden = !long;
    this.more.textContent = this.expanded ? 'Show less' : 'Show more';
    this.more.setAttribute('aria-expanded', String(this.expanded));
  }
}

/** Cut a body at the last paragraph or word boundary before `limit`, never mid-word. */
export function foldAt(body: string, limit: number): string {
  if (body.length <= limit) return body;
  const slice = body.slice(0, limit);
  const paragraph = slice.lastIndexOf('\n\n');
  if (paragraph > limit * 0.5) return slice.slice(0, paragraph);
  const space = slice.lastIndexOf(' ');
  return space > limit * 0.8 ? slice.slice(0, space) : slice;
}

define('caos-message-bubble', CaosMessageBubble);

declare global {
  interface HTMLElementTagNameMap {
    'caos-message-bubble': CaosMessageBubble;
  }
}
